/**
 * Smart Recommendations Component
 * Shows AI-powered product recommendations to increase sales
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Chip, 
  Tooltip,
  CircularProgress,
  Collapse,
  Badge
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FavoriteIcon from '@mui/icons-material/Favorite';
import StarIcon from '@mui/icons-material/Star';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import { getRecommendationsAPI } from '../../API/posAiAPI';
import './SmartRecommendations.css';

const SmartRecommendations = ({ 
  cartItems = [], 
  customerId, 
  orderType,
  onAddToCart,
  isExpanded = false,
  onToggleExpand
}) => {
  const [recommendations, setRecommendations] = useState({
    upsell: [],
    crossSell: [],
    popular: [],
    personalized: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('crossSell');
  const [expanded, setExpanded] = useState(isExpanded);
  const [isFloating, setIsFloating] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Drag handlers
  const handleMouseDown = (e) => {
    if (!isFloating) return;
    setIsDragging(true);
    const rect = dragRef.current?.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - (rect?.left || 0),
      y: e.clientY - (rect?.top || 0)
    };
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const newX = e.clientX - dragOffset.current.x;
    const newY = e.clientY - dragOffset.current.y;
    // Keep within viewport
    const maxX = window.innerWidth - 320;
    const maxY = window.innerHeight - 100;
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove global mouse listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Toggle floating mode
  const toggleFloating = () => {
    if (!isFloating) {
      // Center on screen when first floating
      setPosition({
        x: window.innerWidth - 340,
        y: 100
      });
    }
    setIsFloating(!isFloating);
    setExpanded(true);
  };

  // Fetch recommendations when cart changes
  const fetchRecommendations = useCallback(async () => {
    if (cartItems.length === 0 && !customerId) {
      setRecommendations({ upsell: [], crossSell: [], popular: [], personalized: [] });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getRecommendationsAPI({
        cartItems: cartItems.map(item => ({
          _id: item._id,
          productName: item.productName,
          salesPrice: item.salesPrice,
          categoryId: item.categoryId || item.mainCatgeoryId
        })),
        customerId,
        orderType
      });

      // Handle offline response gracefully
      if (response.offline) {
        console.log('📴 Recommendations offline - skipping');
        // Keep existing recommendations if any, don't show error
        return;
      }

      if (response.success) {
        setRecommendations(response.data);
        
        // Auto-select best tab based on available recommendations
        if (response.data.personalized?.length > 0 && customerId) {
          setActiveTab('personalized');
        } else if (response.data.crossSell?.length > 0) {
          setActiveTab('crossSell');
        } else if (response.data.upsell?.length > 0) {
          setActiveTab('upsell');
        } else {
          setActiveTab('popular');
        }
      }
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
      // Don't show error for network issues - recommendations are optional
      if (!err.message?.includes('Network')) {
        setError('Could not load recommendations');
      }
    } finally {
      setLoading(false);
    }
  }, [cartItems, customerId, orderType]);

  // Debounced fetch on cart change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRecommendations();
    }, 500); // Wait 500ms after cart changes

    return () => clearTimeout(timer);
  }, [fetchRecommendations]);

  const handleAddToCart = (product) => {
    if (onAddToCart) {
      onAddToCart(product);
    }
  };

  const toggleExpand = () => {
    setExpanded(!expanded);
    if (onToggleExpand) {
      onToggleExpand(!expanded);
    }
  };

  // Get total recommendation count
  const totalCount = 
    (recommendations.upsell?.length || 0) +
    (recommendations.crossSell?.length || 0) +
    (recommendations.personalized?.length || 0);

  // Get current recommendations based on active tab
  const currentRecommendations = recommendations[activeTab] || [];

  const tabs = [
    { key: 'crossSell', label: 'Pairs Well', icon: <LocalOfferIcon fontSize="small" />, count: recommendations.crossSell?.length || 0 },
    { key: 'upsell', label: 'Upgrade', icon: <TrendingUpIcon fontSize="small" />, count: recommendations.upsell?.length || 0 },
    { key: 'personalized', label: 'For You', icon: <FavoriteIcon fontSize="small" />, count: recommendations.personalized?.length || 0 },
    { key: 'popular', label: 'Popular', icon: <StarIcon fontSize="small" />, count: recommendations.popular?.length || 0 },
  ];

  if (cartItems.length === 0 && !customerId) {
    return null;
  }

  return (
    <Box 
      ref={dragRef}
      className={`smart-recommendations-container ${isFloating ? 'floating' : ''} ${isDragging ? 'dragging' : ''}`}
      style={isFloating ? {
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 1300,
        width: 320
      } : {}}
    >
      {/* Header */}
      <Box className="smart-recommendations-header">
        {/* Drag Handle */}
        {isFloating && (
          <Box 
            className="drag-handle"
            onMouseDown={handleMouseDown}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        
        <Box className="header-left" onClick={toggleExpand}>
          <Badge badgeContent={totalCount} color="primary" max={9}>
            <AutoAwesomeIcon className="ai-icon" />
          </Badge>
          <Typography variant="subtitle2" className="header-title">
            Smart Suggestions
          </Typography>
        </Box>
        <Box className="header-right">
          <Tooltip title="Refresh">
            <IconButton 
              size="small" 
              onClick={(e) => { e.stopPropagation(); fetchRecommendations(); }}
              disabled={loading}
            >
              <RefreshIcon fontSize="small" className={loading ? 'spinning' : ''} />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFloating ? "Dock panel" : "Float panel"}>
            <IconButton 
              size="small" 
              onClick={(e) => { e.stopPropagation(); toggleFloating(); }}
            >
              <DragIndicatorIcon fontSize="small" style={{ color: isFloating ? '#6366f1' : undefined }} />
            </IconButton>
          </Tooltip>
          {isFloating ? (
            <Tooltip title="Close">
              <IconButton size="small" onClick={() => setIsFloating(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton size="small" onClick={toggleExpand}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box className="smart-recommendations-content">
          {/* Tabs */}
          <Box className="recommendation-tabs">
            {tabs.map(tab => (
              <Chip
                key={tab.key}
                icon={tab.icon}
                label={`${tab.label} (${tab.count})`}
                size="small"
                variant={activeTab === tab.key ? 'filled' : 'outlined'}
                color={activeTab === tab.key ? 'primary' : 'default'}
                onClick={() => setActiveTab(tab.key)}
                className="tab-chip"
              />
            ))}
          </Box>

          {/* Loading */}
          {loading && (
            <Box className="loading-container">
              <CircularProgress size={24} />
              <Typography variant="caption">Analyzing...</Typography>
            </Box>
          )}

          {/* Error */}
          {error && !loading && (
            <Typography variant="caption" color="error" className="error-text">
              {error}
            </Typography>
          )}

          {/* Recommendations List */}
          {!loading && !error && (
            <Box className="recommendations-list">
              {currentRecommendations.length === 0 ? (
                <Typography variant="caption" className="no-recommendations">
                  No suggestions available
                </Typography>
              ) : (
                currentRecommendations.map((product, index) => (
                  <Box key={product._id || index} className="recommendation-item">
                    <Box className="product-info">
                      {product.imageUrl && (
                        <img 
                          src={product.imageUrl} 
                          alt={product.productName}
                          className="product-image"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <Box className="product-details">
                        <Typography variant="body2" className="product-name">
                          {product.productName}
                        </Typography>
                        <Typography variant="caption" className="product-reason">
                          {product.reason}
                        </Typography>
                        <Typography variant="body2" className="product-price">
                          ₹{product.salesPrice?.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                    <Tooltip title="Add to cart">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleAddToCart(product)}
                        className="add-btn"
                      >
                        <AddShoppingCartIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default SmartRecommendations;
